import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import TM from "@/components/TM";
import { useRole } from "@/contexts/RoleContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeacherLogin() {
  const navigate = useNavigate();
  const { setTeacherId } = useRole();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API}/api/auth/teacher/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email: email.trim(), 
          password: password 
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Invalid email or password");
      }

      // Store token and teacher info
      sessionStorage.setItem("teacher_token", data.token);
      sessionStorage.setItem("teacher_id", data.teacher_id);
      sessionStorage.setItem("teacher_name", data.name || "");
      sessionStorage.setItem("teacher_email", data.email || "");

      // Sync React context so TeacherDashboard gets correct teacherId without page reload
      setTeacherId(data.teacher_id);

      // Show teacher welcome screen before landing on dashboard
      navigate("/teacher/welcome", {
        state: { name: data.name || "Teacher" },
      });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(to bottom, #1c2b3a 0%, #2e3f52 50%, #3d5166 100%)" }}
      data-testid="teacher-login-page"
    >
      <div className="w-full max-w-md">
        <div
          className="rounded-3xl p-8 border-0 mit-shadow bg-white"
        >
          {/* Icon + title */}
          <div className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-5 bg-slate-100"
            >
              <GraduationCap size={32} className="text-slate-700" />
            </div>
            <h1 className="font-display text-3xl font-bold text-slate-800 tracking-tight">
              Teacher Login
            </h1>
            <p className="text-sm mt-2 text-slate-600">
              Access your Mitraa<TM /> Dashboard
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="teacher-email"
                className="block text-sm font-semibold mb-2 text-slate-700"
              >
                Email
              </label>
              <input
                id="teacher-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teacher@school.edu"
                className="w-full px-4 py-3 rounded-xl text-sm text-slate-800 placeholder-slate-400 border border-slate-200 outline-none transition-all focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                data-testid="teacher-login-email"
              />
            </div>

            <div>
              <label
                htmlFor="teacher-password"
                className="block text-sm font-semibold mb-2 text-slate-700"
              >
                Password
              </label>
              <input
                id="teacher-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-4 py-3 rounded-xl text-sm text-slate-800 placeholder-slate-400 border border-slate-200 outline-none transition-all focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
                data-testid="teacher-login-password"
              />
            </div>

            {error && (
              <div
                className="text-sm px-4 py-3 rounded-xl bg-red-50 text-red-600 border border-red-100"
                data-testid="teacher-login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-slate-800 hover:bg-slate-900 text-white transition-all mt-2"
              data-testid="teacher-login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>

            {/* Back link — inside card */}
            <div className="border-t border-slate-100 pt-5 mt-2 text-center">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="back-to-welcome-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Welcome
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs mt-6 text-slate-400">
          Mitraa<TM /> Teacher Portal — Empowering educators
        </p>
      </div>
    </div>
  );
}
